import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { UserPlus, X, RefreshCw, User, Building2, Calendar, Phone, MapPin } from 'lucide-react';
import { UserTableRow } from './UserTableRow';
import { StatusBadge } from '@/components/common/StatusBadge';
import {
  useUsers,
  useUser,
  useCreateUser,
  useUpdateUser,
  useBlockUser,
  useUnblockUser,
  useDeleteUser,
} from '@/hooks/useUsers';
import { formatDateTime } from '@/utils/formatters';
import type { UserStatus } from '@/types/user.types';

export function Admin() {
  const [selectedFilter, setSelectedFilter] = useState<'all' | UserStatus>('all');
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Form state for new user
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '',
    cargo: 'Proprietário',
    property: {
      name: '',
      city: '',
      uf: '',
      area_total: 0,
      reserva_legal: 0,
      sistema_ilpf: 0,
      pastagem_ha: 0,
      area_agricola: 0,
    },
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    cargo: '',
    property: {
      name: '',
      city: '',
      uf: '',
      area_total: 0,
      reserva_legal: 0,
      sistema_ilpf: 0,
      pastagem_ha: 0,
      area_agricola: 0,
    },
  });

  // Block form state
  const [blockReason, setBlockReason] = useState('');

  const { data, isLoading, error } = useUsers({
    status: selectedFilter !== 'all' ? selectedFilter : undefined,
  });

  const { data: selectedUserData } = useUser(selectedUserId || '');

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();
  const deleteUserMutation = useDeleteUser();

  useEffect(() => {
    // User data loaded
  }, [selectedUserData]);

  useEffect(() => {
    if (selectedUserData && showEditModal) {
      const propertyData =
        typeof selectedUserData.property === 'string'
          ? {
              name: selectedUserData.property,
              city: '',
              uf: '',
              area_total: 0,
              reserva_legal: 0,
              sistema_ilpf: 0,
              pastagem_ha: 0,
              area_agricola: 0,
            }
          : selectedUserData.property;

      setEditFormData({
        name: selectedUserData.name || '',
        phone: selectedUserData.phone || '',
        cargo: selectedUserData.cargo || '',
        property: {
          name: propertyData?.name || '',
          city: propertyData?.city || '',
          uf: propertyData?.uf || '',
          area_total: propertyData?.area_total || 0,
          reserva_legal: propertyData?.reserva_legal || 0,
          sistema_ilpf: propertyData?.sistema_ilpf || 0,
          pastagem_ha: propertyData?.pastagem_ha || 0,
          area_agricola: propertyData?.area_agricola || 0,
        },
      });
    }
  }, [selectedUserData, showEditModal]);

  const users = data?.users || [];

  const handleCreateUser = async () => {
    if (!formData.name || !formData.phone || !formData.password || !formData.property.name) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      await createUserMutation.mutateAsync(formData);
      setShowNewUserModal(false);
      // Reset form
      setFormData({
        name: '',
        phone: '',
        password: '',
        cargo: 'Proprietário',
        property: {
          name: '',
          city: '',
          uf: '',
          area_total: 0,
          reserva_legal: 0,
          sistema_ilpf: 0,
          pastagem_ha: 0,
          area_agricola: 0,
        },
      });
    } catch (err) {
      console.error('Error creating user:', err);
      alert('Erro ao criar usuário. Tente novamente.');
    }
  };

  const handleBlockUser = async () => {
    if (!selectedUserId || !blockReason || blockReason.length < 10) {
      alert('Por favor, forneça um motivo com pelo menos 10 caracteres');
      return;
    }

    try {
      await blockUserMutation.mutateAsync({
        id: selectedUserId,
        data: {
          reason: blockReason,
          revoke_sessions: true,
        },
      });
      setShowBlockModal(false);
      setBlockReason('');
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Erro ao bloquear usuário. Tente novamente.');
    }
  };

  const handleUnblockUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowUnblockModal(true);
  };

  const confirmUnblockUser = async () => {
    if (!selectedUserId) return;

    try {
      await unblockUserMutation.mutateAsync(selectedUserId);
      setShowUnblockModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error unblocking user:', err);
      alert('Erro ao desbloquear usuário. Tente novamente.');
    }
  };

  const handleDeleteUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUserId) return;

    try {
      await deleteUserMutation.mutateAsync(selectedUserId);
      setShowDeleteModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Erro ao excluir usuário. Tente novamente.');
    }
  };

  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailsModal(true);
  };

  const handleEditUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUserId) return;

    try {
      await updateUserMutation.mutateAsync({
        id: selectedUserId,
        data: editFormData,
      });
      setShowEditModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Erro ao atualizar usuário. Tente novamente.');
    }
  };

  if (error) {
    return (
      <Layout
        breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Gerenciamento de Usuários' }]}
      >
        <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar usuários
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar a lista de usuários. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Gerenciamento de Usuários' }]}
    >
      <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6">
        {/* Page Heading */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
            Gerenciamento de Usuários
          </h1>
          <button
            onClick={() => setShowNewUserModal(true)}
            className="flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            <UserPlus size={18} />
            <span>Novo usuário</span>
          </button>
        </div>

        {/* Filter Chips */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por status">
          {(
            [
              { value: 'all' as const, label: 'Todos', count: users.length },
              { value: 'active' as const, label: 'Ativo', count: users.filter((u) => u.status === 'active').length },
              { value: 'inactive' as const, label: 'Inativo', count: users.filter((u) => u.status === 'inactive').length },
              { value: 'blocked' as const, label: 'Bloqueado', count: users.filter((u) => u.status === 'blocked').length },
              { value: 'suspended' as const, label: 'Suspenso', count: users.filter((u) => u.status === 'suspended').length },
            ]
          ).map((filter) => {
            const active = selectedFilter === filter.value;
            return (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                aria-pressed={active}
                className={`flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-card text-text-secondary hover:bg-muted'
                }`}
              >
                <span>{filter.label}</span>
                <span className={`tabular-nums ${active ? 'text-white/80' : 'text-text-hint'}`}>
                  {filter.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Users Table */}
        <div className="w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="size-8 animate-spin text-primary" />
            </div>
          ) : users.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              <table className="w-full table-fixed text-sm">
                <thead className="sticky top-0 bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="w-[22%] px-6 py-3">Usuário</th>
                    <th className="w-[12%] px-6 py-3">Status</th>
                    <th className="w-[12%] px-6 py-3">Cargo</th>
                    <th className="w-[18%] px-6 py-3">Propriedade</th>
                    <th className="w-[16%] px-6 py-3">Último Acesso</th>
                    <th className="w-[10%] px-6 py-3">Verificação</th>
                    <th className="w-[10%] px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      onViewDetails={handleViewDetails}
                      onEdit={handleEditUser}
                      onBlock={(id) => {
                        setSelectedUserId(id);
                        setShowBlockModal(true);
                      }}
                      onUnblock={handleUnblockUser}
                      onDelete={handleDeleteUser}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-lg font-medium mb-2" style={{ color: '#1B263B' }}>
                  Nenhum usuário encontrado
                </p>
                <p className="text-sm" style={{ color: '#57636C' }}>
                  Não há usuários cadastrados com os filtros selecionados
                </p>
              </div>
            </div>
          )}
        </div>

        {/* New User Modal */}
        {showNewUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-card shadow-xl max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-border p-6 sticky top-0 bg-card z-10">
                <h2 className="text-2xl font-semibold text-text-title">Novo Usuário</h2>
                <button
                  onClick={() => setShowNewUserModal(false)}
                  className="rounded-full p-2 hover:bg-muted"
                  disabled={createUserMutation.isPending}
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateUser();
                  }}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-text-title">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="Digite o nome completo"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-text-title">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="+5511999999999"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-text-title">
                        Senha *
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="Mínimo 6 caracteres"
                        required
                        minLength={6}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-text-title">
                        Cargo *
                      </label>
                      <select
                        value={formData.cargo}
                        onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                        className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        required
                      >
                        <option value="Proprietário">Proprietário</option>
                        <option value="Gestor">Gestor</option>
                        <option value="Capataz">Capataz</option>
                        <option value="Vaqueiro">Vaqueiro</option>
                        <option value="Ordenhador">Ordenhador</option>
                        <option value="Salgueiro">Salgueiro</option>
                        <option value="Técnico">Técnico</option>
                        <option value="Veterinário">Veterinário</option>
                      </select>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4">
                    <h3 className="text-lg font-semibold text-text-title mb-4">
                      Dados da Propriedade
                    </h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Nome da Propriedade *
                        </label>
                        <input
                          type="text"
                          value={formData.property.name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: { ...formData.property, name: e.target.value },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Ex: Fazenda Santa Maria"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Cidade *
                        </label>
                        <input
                          type="text"
                          value={formData.property.city}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: { ...formData.property, city: e.target.value },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Ex: Cuiabá"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          UF *
                        </label>
                        <input
                          type="text"
                          value={formData.property.uf}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: { ...formData.property, uf: e.target.value.toUpperCase() },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Ex: MT"
                          maxLength={2}
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Área Total (ha)
                        </label>
                        <input
                          type="number"
                          value={formData.property.area_total || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: {
                                ...formData.property,
                                area_total: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Pastagem (ha)
                        </label>
                        <input
                          type="number"
                          value={formData.property.pastagem_ha || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: {
                                ...formData.property,
                                pastagem_ha: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Reserva Legal (ha)
                        </label>
                        <input
                          type="number"
                          value={formData.property.reserva_legal || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: {
                                ...formData.property,
                                reserva_legal: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Sistema ILPF (ha)
                        </label>
                        <input
                          type="number"
                          value={formData.property.sistema_ilpf || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: {
                                ...formData.property,
                                sistema_ilpf: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Área Agrícola (ha)
                        </label>
                        <input
                          type="number"
                          value={formData.property.area_agricola || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              property: {
                                ...formData.property,
                                area_agricola: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 border-t border-border p-6 sticky bottom-0 bg-card">
                <button
                  onClick={() => setShowNewUserModal(false)}
                  disabled={createUserMutation.isPending}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={createUserMutation.isPending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#003E6B' }}
                >
                  {createUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
                  {createUserMutation.isPending ? 'Salvando...' : 'Salvar Usuário'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Block User Modal */}
        {showBlockModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-card shadow-xl">
              <div className="flex items-center justify-between border-b border-border p-6">
                <h2 className="text-xl font-semibold text-text-title">Bloquear Usuário</h2>
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setBlockReason('');
                    setSelectedUserId(null);
                  }}
                  className="rounded-full p-2 hover:bg-muted"
                  disabled={blockUserMutation.isPending}
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              <div className="p-6">
                <label className="mb-2 block text-sm font-medium text-text-title">
                  Motivo do Bloqueio *
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  rows={4}
                  placeholder="Mínimo 10 caracteres"
                  minLength={10}
                  required
                />
                <p className="text-xs text-text-secondary mt-1">
                  Todas as sessões do usuário serão invalidadas
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-border p-6">
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setBlockReason('');
                    setSelectedUserId(null);
                  }}
                  disabled={blockUserMutation.isPending}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBlockUser}
                  disabled={blockUserMutation.isPending || blockReason.length < 10}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#FE5963' }}
                >
                  {blockUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
                  {blockUserMutation.isPending ? 'Bloqueando...' : 'Bloquear'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {showDetailsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-3xl rounded-lg bg-card shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-border p-6 sticky top-0 bg-card z-10">
                <h2 className="text-2xl font-semibold text-text-title">Detalhes do Usuário</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedUserId(null);
                  }}
                  className="rounded-full p-2 hover:bg-muted"
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              {!selectedUserData ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="size-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* User Info */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: '#F1F4F8' }}>
                        <User size={24} style={{ color: '#003E6B' }} />
                      </div>
                      <h3 className="text-lg font-semibold text-text-title">
                        Informações Pessoais
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-text-secondary">
                          Nome Completo
                        </label>
                        <p className="text-base text-text-title mt-1">
                          {selectedUserData.name || 'Não informado'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-secondary">Telefone</label>
                        <p className="text-base text-text-title mt-1 flex items-center gap-2">
                          <Phone size={16} />
                          {selectedUserData.phone || 'Não informado'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-secondary">Cargo</label>
                        <p className="text-base text-text-title mt-1">
                          {selectedUserData.cargo || selectedUserData.role || 'Não informado'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-secondary">Status</label>
                        <div className="mt-1">
                          {selectedUserData.status ? (
                            <StatusBadge status={selectedUserData.status} />
                          ) : (
                            <span className="text-text-secondary">Não informado</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-secondary">
                          Verificado
                        </label>
                        <p className="text-base text-text-title mt-1">
                          {selectedUserData.verified ? 'Sim' : 'Não'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-text-secondary">
                          Último Acesso
                        </label>
                        <p className="text-base text-text-title mt-1 flex items-center gap-2">
                          <Calendar size={16} />
                          {formatDateTime(selectedUserData.lastAccess)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Property Info */}
                  {selectedUserData.property && (
                    <div className="border-t pt-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F1F4F8' }}>
                          <Building2 size={24} style={{ color: '#7AA41F' }} />
                        </div>
                        <h3 className="text-lg font-semibold text-text-title">Propriedade</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-text-secondary">
                            Nome da Propriedade
                          </label>
                          <p className="text-base text-text-title mt-1">
                            {typeof selectedUserData.property === 'string'
                              ? selectedUserData.property
                              : selectedUserData.property?.name || 'Não informado'}
                          </p>
                        </div>
                        {typeof selectedUserData.property === 'object' && (
                          <>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Localização
                              </label>
                              <p className="text-base text-text-title mt-1 flex items-center gap-2">
                                <MapPin size={16} />
                                {selectedUserData.property.city || 'N/A'},{' '}
                                {selectedUserData.property.uf || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Área Total
                              </label>
                              <p className="text-base text-text-title mt-1">
                                {selectedUserData.property.area_total || 0} ha
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Pastagem
                              </label>
                              <p className="text-base text-text-title mt-1">
                                {selectedUserData.property.pastagem_ha || 0} ha
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Reserva Legal
                              </label>
                              <p className="text-base text-text-title mt-1">
                                {selectedUserData.property.reserva_legal || 0} ha
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Sistema ILPF
                              </label>
                              <p className="text-base text-text-title mt-1">
                                {selectedUserData.property.sistema_ilpf || 0} ha
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">
                                Área Agrícola
                              </label>
                              <p className="text-base text-text-title mt-1">
                                {selectedUserData.property.area_agricola || 0} ha
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-border p-6 sticky bottom-0 bg-card">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedUserId(null);
                  }}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUserData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-3xl rounded-lg bg-card shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-border p-6 sticky top-0 bg-card z-10">
                <h2 className="text-2xl font-semibold text-text-title">Editar Usuário</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUserId(null);
                  }}
                  className="rounded-full p-2 hover:bg-muted"
                  disabled={updateUserMutation.isPending}
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              <div className="p-6">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleUpdateUser();
                  }}
                >
                  {/* User Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-text-title mb-4">
                      Informações Pessoais
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Nome Completo *
                        </label>
                        <input
                          type="text"
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, name: e.target.value })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Telefone *
                        </label>
                        <input
                          type="tel"
                          value={editFormData.phone}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, phone: e.target.value })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Cargo *
                        </label>
                        <select
                          value={editFormData.cargo}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, cargo: e.target.value })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          required
                        >
                          <option value="Proprietário">Proprietário</option>
                          <option value="Gestor">Gestor</option>
                          <option value="Capataz">Capataz</option>
                          <option value="Vaqueiro">Vaqueiro</option>
                          <option value="Ordenhador">Ordenhador</option>
                          <option value="Salgueiro">Salgueiro</option>
                          <option value="Técnico">Técnico</option>
                          <option value="Veterinário">Veterinário</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Property Info */}
                  <div className="border-t border-border pt-4">
                    <h3 className="text-lg font-semibold text-text-title mb-4">
                      Dados da Propriedade
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Nome da Propriedade *
                        </label>
                        <input
                          type="text"
                          value={editFormData.property.name}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: { ...editFormData.property, name: e.target.value },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Cidade *
                        </label>
                        <input
                          type="text"
                          value={editFormData.property.city}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: { ...editFormData.property, city: e.target.value },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          UF *
                        </label>
                        <input
                          type="text"
                          value={editFormData.property.uf}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                uf: e.target.value.toUpperCase(),
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          maxLength={2}
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Área Total (ha)
                        </label>
                        <input
                          type="number"
                          value={editFormData.property.area_total || ''}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                area_total: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Pastagem (ha)
                        </label>
                        <input
                          type="number"
                          value={editFormData.property.pastagem_ha || ''}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                pastagem_ha: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Reserva Legal (ha)
                        </label>
                        <input
                          type="number"
                          value={editFormData.property.reserva_legal || ''}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                reserva_legal: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Sistema ILPF (ha)
                        </label>
                        <input
                          type="number"
                          value={editFormData.property.sistema_ilpf || ''}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                sistema_ilpf: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-text-title">
                          Área Agrícola (ha)
                        </label>
                        <input
                          type="number"
                          value={editFormData.property.area_agricola || ''}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              property: {
                                ...editFormData.property,
                                area_agricola: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-lg border border-border-strong px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="flex justify-end gap-3 border-t border-border p-6 sticky bottom-0 bg-card">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUserId(null);
                  }}
                  disabled={updateUserMutation.isPending}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdateUser}
                  disabled={updateUserMutation.isPending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#003E6B' }}
                >
                  {updateUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
                  {updateUserMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unblock User Confirmation Modal */}
        {showUnblockModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-card shadow-xl">
              <div className="flex items-center justify-between border-b border-border p-6">
                <h2 className="text-xl font-semibold text-text-title">Desbloquear Usuário</h2>
                <button
                  onClick={() => {
                    setShowUnblockModal(false);
                    setSelectedUserId(null);
                  }}
                  className="rounded-full p-2 hover:bg-muted"
                  disabled={unblockUserMutation.isPending}
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-sm text-text-body">
                  Deseja realmente desbloquear este usuário? Ele poderá acessar o sistema novamente.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-border p-6">
                <button
                  onClick={() => {
                    setShowUnblockModal(false);
                    setSelectedUserId(null);
                  }}
                  disabled={unblockUserMutation.isPending}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmUnblockUser}
                  disabled={unblockUserMutation.isPending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#249689' }}
                >
                  {unblockUserMutation.isPending && (
                    <RefreshCw size={16} className="animate-spin" />
                  )}
                  {unblockUserMutation.isPending ? 'Desbloqueando...' : 'Desbloquear'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-card shadow-xl">
              <div className="flex items-center justify-between border-b border-border p-6">
                <h2 className="text-xl font-semibold text-text-title">Excluir Usuário</h2>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUserId(null);
                  }}
                  className="rounded-full p-2 hover:bg-muted"
                  disabled={deleteUserMutation.isPending}
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-sm text-text-body mb-3">
                  <strong>Atenção!</strong> Esta ação não pode ser desfeita.
                </p>
                <p className="text-sm text-text-body">
                  Deseja realmente excluir este usuário? Todos os dados associados serão
                  permanentemente removidos.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-border p-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUserId(null);
                  }}
                  disabled={deleteUserMutation.isPending}
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-title hover:bg-muted/50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteUser}
                  disabled={deleteUserMutation.isPending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#FE5963' }}
                >
                  {deleteUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
                  {deleteUserMutation.isPending ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
